import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Input,
  Label,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const AnalyticsByDomain = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("sessions");
  const [sortDirection, setSortDirection] = useState("desc");
  const itemsPerPage = 15;

  const fetchAnalytics = async (custom = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await axios.post(
        `${API_URL}/analytics/domain`,
        {
          startDate: custom ? startDate || "yesterday" : "yesterday",
          endDate: custom ? endDate || "yesterday" : "yesterday",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setData(res.data);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erro ao buscar relatório por domínio:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(false);
  }, []);

  // 🧠 Ordenação avançada
  const isISODate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
  const toNumber = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedData = [...data].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    if (!isNaN(toNumber(valA)) && !isNaN(toNumber(valB))) {
      valA = toNumber(valA);
      valB = toNumber(valB);
      return sortDirection === "asc" ? valA - valB : valB - valA;
    }

    if (isISODate(valA) && isISODate(valB)) {
      return sortDirection === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    valA = (valA ?? "").toString().toLowerCase();
    valB = (valB ?? "").toString().toLowerCase();
    return sortDirection === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 🔢 Agrupa dados por domínio (soma métricas)
  const aggregatedByDomain = Object.values(
    data.reduce((acc, item) => {
      const host = item.host || "(sem domínio)";
      if (!acc[host]) {
        acc[host] = {
          host,
          sessions: 0,
          activeUsers: 0,
          events: 0,
          avgSessionDurationTotal: 0,
          avgSessionDurationCount: 0,
        };
      }
      acc[host].sessions += parseInt(item.sessions || 0, 10);
      acc[host].activeUsers += parseInt(item.activeUsers || 0, 10);
      acc[host].events += parseInt(item.events || 0, 10);

      const dur = parseFloat(item.avgSessionDuration || 0);
      if (!isNaN(dur)) {
        acc[host].avgSessionDurationTotal += dur;
        acc[host].avgSessionDurationCount += 1;
      }

      return acc;
    }, {})
  ).map((v) => ({
    host: v.host,
    sessions: v.sessions,
    activeUsers: v.activeUsers,
    events: v.events,
    avgSessionDuration:
      v.avgSessionDurationCount > 0
        ? v.avgSessionDurationTotal / v.avgSessionDurationCount
        : 0,
  }));

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtro de datas */}
        <Row className="mb-4">
          <Col md="3">
            <Label className="font-weight-bold text-dark">Início:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Fim:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col md="3" className="d-flex align-items-end justify-content-start">
            <Button color="primary" onClick={() => fetchAnalytics(true)}>
              🔍 Consultar
            </Button>
          </Col>
        </Row>

        {/* 📊 Gráfico */}
        <Row>
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">🌐 Sessões por Domínio</h3>
              </CardHeader>
              <CardBody style={{ minHeight: 300 }}>
                {loading ? (
                  <Loader />
                ) : data.length === 0 ? (
                  <p className="text-center text-muted">Sem dados.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={aggregatedByDomain}>
                      <XAxis dataKey="host" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">📋 Detalhamento por Domínio</h3>
              </CardHeader>

              {loading ? (
                <Loader />
              ) : (
                <>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        {[
                          { key: "date", label: "📅 Data" },
                          { key: "host", label: "🌐 Domínio" },
                          { key: "activeUsers", label: "👥 Usuários" },
                          { key: "sessions", label: "📍 Sessões" },
                          { key: "events", label: "⚡ Eventos" },
                          { key: "avgSessionDuration", label: "⏱ Duração Média" },
                        ].map((col) => (
                          <th
                            key={col.key}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => handleSort(col.key)}
                            title={`Ordenar por ${col.label.replace(/^[^\s]+\s/, "")}`}
                          >
                            {col.label}{" "}
                            {sortColumn === col.key
                              ? sortDirection === "asc"
                                ? "▲"
                                : "▼"
                              : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            {row.date
                              ? new Date(row.date).toLocaleDateString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                              : "-"}
                          </td>
                          <td>{row.host}</td>
                          <td>{row.activeUsers}</td>
                          <td>{row.sessions}</td>
                          <td>{row.events}</td>
                          <td>{parseFloat(row.avgSessionDuration).toFixed(1)}s</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 📄 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              setCurrentPage(currentPage - 1);
                          }}
                        >
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {(() => {
                        const visiblePages = 5;
                        const pages = [];
                        let start = Math.max(
                          1,
                          currentPage - Math.floor(visiblePages / 2)
                        );
                        let end = Math.min(totalPages, start + visiblePages - 1);

                        if (end - start < visiblePages - 1) {
                          start = Math.max(1, end - visiblePages + 1);
                        }

                        if (start > 1) {
                          pages.push(
                            <PaginationItem key="start-ellipsis" disabled>
                              <PaginationLink href="#">...</PaginationLink>
                            </PaginationItem>
                          );
                        }

                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <PaginationItem
                              key={i}
                              active={currentPage === i}
                            >
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentPage(i);
                                }}
                              >
                                {i}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }

                        if (end < totalPages) {
                          pages.push(
                            <PaginationItem key="end-ellipsis" disabled>
                              <PaginationLink href="#">...</PaginationLink>
                            </PaginationItem>
                          );
                        }

                        return pages;
                      })()}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages)
                              setCurrentPage(currentPage + 1);
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AnalyticsByDomain;